import { Button, Modal, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCheckCircle, AiOutlineMinusCircle } from "react-icons/ai";

import type { SandboxAdminUser } from "shell/console/types";

import { useCustomNotification } from "design-system/hooks";
import {
  useSandboxAdminUsers,
  useUpdateSandboxAdminUserStatus,
} from "shell/console/api/sandboxAdmin/hooks";
import { SandboxAdminTableLayout } from "shell/console/features/shared/SandboxAdminTableLayout";
import { formatDateToCustomFormat } from "shell/platform/utils/dateFormatter";
import { extractRestErrorMessage } from "shell/platform/utils/utils";

const statusConfig: Record<
  SandboxAdminUser["status"],
  { color: string; icon: React.ReactNode }
> = {
  active: { color: "green", icon: <AiOutlineCheckCircle /> },
  inactive: { color: "red", icon: <AiOutlineMinusCircle /> },
};

export const UserManagement = () => {
  const { t } = useTranslation("common");
  const { data, isLoading, error, refetch, isFetching } =
    useSandboxAdminUsers();
  const { mutateAsync: updateStatus, isPending: isUpdating } =
    useUpdateSandboxAdminUserStatus();
  const { showNotification } = useCustomNotification();

  const [search, setSearch] = useState("");

  const users = data?.users ?? [];

  const query = search.trim().toLowerCase();

  const filteredUsers = query
    ? users.filter(user =>
        [
          user.first_name,
          user.last_name,
          user.email,
          user.company_name,
          user.title,
        ].some(field => field?.toLowerCase().includes(query)),
      )
    : users;

  const queryErrorMessage = error
    ? extractRestErrorMessage(error) || error.message || t("errors.unknown")
    : null;

  const handleToggleStatus = (user: SandboxAdminUser) => {
    const isActive = user.status === "active";

    const performUpdate = async () => {
      try {
        await updateStatus({
          user_id: user.user_id,
          status: isActive ? "inactive" : "active",
        });
        showNotification({
          type: "success",
          message: isActive
            ? t("components.sandboxAdmin.userManagement.deactivated")
            : t("components.sandboxAdmin.userManagement.activated"),
          description: `${user.first_name} ${user.last_name}`,
        });
        await refetch();
      } catch (err) {
        showNotification({
          type: "error",
          message: t("components.sandboxAdmin.userManagement.updateFailed"),
          description:
            extractRestErrorMessage(err) ??
            (err instanceof Error ? err.message : undefined) ??
            "",
        });
      }
    };

    if (isActive) {
      Modal.confirm({
        title: t("components.sandboxAdmin.userManagement.deactivateTitle"),
        content: t("components.sandboxAdmin.userManagement.deactivateContent", {
          name: `${user.first_name} ${user.last_name}`,
        }),
        okText: t("components.buttons.deactivate"),
        okType: "danger",
        onOk: performUpdate,
      });
      return;
    }

    performUpdate();
  };

  const columns = [
    {
      title: t("labels.firstName"),
      dataIndex: "first_name",
      key: "firstName",
    },
    {
      title: t("labels.lastName"),
      dataIndex: "last_name",
      key: "lastName",
    },
    {
      title: t("labels.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("components.addressFields.country"),
      dataIndex: "country",
      key: "country",
    },
    {
      title: t("components.sandboxAdmin.userManagement.verifiedEmail"),
      dataIndex: "email_verified",
      key: "verifiedEmail",
      render: (emailVerified: boolean) =>
        emailVerified ? (
          <Tag color="green">{t("labels.yes")}</Tag>
        ) : (
          <Tag color="red">{t("labels.no")}</Tag>
        ),
    },
    {
      title: t("labels.title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: t("labels.companyName"),
      dataIndex: "company_name",
      key: "companyName",
    },
    {
      title: t("labels.status"),
      dataIndex: "status",
      key: "status",
      render: (status: SandboxAdminUser["status"]) => {
        const statusInfo = statusConfig[status];
        return (
          <Tag icon={statusInfo.icon} color={statusInfo.color}>
            {t(`status.${status}`)}
          </Tag>
        );
      },
    },
    {
      title: t("labels.createdAt"),
      dataIndex: "created_at",
      key: "createdAt",
      render: (createdAt: string) => formatDateToCustomFormat(createdAt),
    },
    {
      title: t("labels.lastActiveAt"),
      dataIndex: "last_active_at",
      key: "lastActiveAt",
      render: (lastActiveAt: string | null) =>
        lastActiveAt
          ? formatDateToCustomFormat(lastActiveAt)
          : t("labels.noDataAvailable"),
    },
    {
      title: t("labels.actions"),
      key: "actions",
      align: "center" as const,
      render: (_: unknown, record: SandboxAdminUser) => (
        <Button
          size="small"
          danger={record.status === "active"}
          icon={
            record.status === "active" ? (
              <AiOutlineMinusCircle />
            ) : (
              <AiOutlineCheckCircle />
            )
          }
          loading={isUpdating}
          onClick={() => handleToggleStatus(record)}
          data-testid={`toggle-status-${record.user_id}`}
        >
          {record.status === "active"
            ? t("components.buttons.deactivate")
            : t("components.buttons.activate")}
        </Button>
      ),
    },
  ];

  return (
    <SandboxAdminTableLayout
      title={t("components.sandboxAdmin.userManagement.title")}
      subtitle={t("components.sandboxAdmin.userManagement.subtitle")}
      data={filteredUsers}
      columns={columns}
      loading={isLoading}
      rowKey="user_id"
      errorMessage={queryErrorMessage}
      onRetry={() => refetch()}
      retryLoading={isFetching}
      searchInput={{
        placeholder: t(
          "components.sandboxAdmin.userManagement.searchPlaceholder",
        ),
        value: search,
        onChange: setSearch,
        testId: "search-users",
      }}
      refreshTestId="refresh-users"
      testId="sandbox-user-management-table"
    />
  );
};
